/** Client-side validation for GET /auth/methods?surface=web. */
import type { AuthIdentity, AuthMethods } from '@/generated/types'

export type AuthMethod = AuthMethods['methods'][number]
export type WebAuthMethod = AuthMethod & { id: 'google' | 'password' }

const isWebMethod = (value: unknown): value is WebAuthMethod => {
  if (!value || typeof value !== 'object') return false
  const method = value as Partial<AuthMethod>
  return (
    (method.id === 'google' && method.presentation === 'primary') ||
    (method.id === 'password' && method.presentation === 'breakGlass')
  )
}

/**
 * Keep the Server's order, remove malformed/duplicate entries, and never
 * invent a fallback. Apple is native-only and cannot render on Web.
 */
export function offeredMethods(payload: unknown): WebAuthMethod[] {
  if (!payload || typeof payload !== 'object') return []
  const response = payload as Partial<AuthMethods>
  if (response.surface !== 'web' || !Array.isArray(response.methods)) return []
  const seen = new Set<WebAuthMethod['id']>()
  return response.methods.filter((method): method is WebAuthMethod => {
    if (!isWebMethod(method) || seen.has(method.id)) return false
    seen.add(method.id)
    return true
  })
}

/**
 * Reauthentication is account-scoped, unlike the deployment-wide discovery
 * response. Google is safe only when that subject is attached to this user.
 * Password is safe only for a password-only account; in particular, never
 * infer it for an Apple-only account merely because Google is absent.
 */
export function reauthenticationMethods(
  identities: AuthIdentity[],
  payload: unknown,
): { google: boolean; password: boolean } {
  const methods = offeredMethods(payload)
  return {
    google:
      identities.some((identity) => identity.provider === 'google') &&
      methods.some((method) => method.id === 'google'),
    password: identities.length === 0 && methods.some((method) => method.id === 'password'),
  }
}
